import React, { useState } from 'react';
import { useToast } from '../../../components/Toast';
import './Forms.css';

/**
 * ImageUploadField Component
 * Handles image upload for form fields
 * Displays preview and allows user to select from uploaded images
 */
export default function ImageUploadField({
  value = '',
  onChange,
  label = 'Image',
  folder = 'general',
  apiBase
}) {
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [availableImages, setAvailableImages] = useState([]);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await fetch(`${apiBase}/homepage/cms/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();
      if (result.ok && result.data?.url) {
        const imageUrl = result.data.url;
        setPreviewUrl(imageUrl);
        onChange(imageUrl);
        toast.success('✓ Image uploaded');
        // Refresh gallery
        await loadGallery();
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const loadGallery = async () => {
    setLoadingGallery(true);
    try {
      const response = await fetch(
        `${apiBase}/homepage/cms/media?folder=${folder}&type=image`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load gallery');

      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        setAvailableImages(result.data);
      }
    } catch (err) {
      console.error('Gallery load error:', err);
      toast.error('Failed to load images');
    } finally {
      setLoadingGallery(false);
    }
  };

  const handleSelectFromGallery = async () => {
    if (availableImages.length === 0) {
      await loadGallery();
    }
    setShowImageGallery(!showImageGallery);
  };

  const selectImage = (imageUrl) => {
    setPreviewUrl(imageUrl);
    onChange(imageUrl);
    setShowImageGallery(false);
    toast.success('✓ Image selected');
  };

  return (
    <div className="image-upload-field">
      <label className="form-label">{label}</label>

      {/* Preview */}
      {previewUrl && (
        <div className="image-preview-container">
          <img src={previewUrl} alt="Preview" className="image-preview" />
          <button
            type="button"
            onClick={() => {
              setPreviewUrl('');
              onChange('');
            }}
            className="btn-remove-image"
            title="Remove image"
          >
            ✕
          </button>
        </div>
      )}

      {/* Upload Input */}
      <div className="form-group">
        <label className="upload-label">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={isUploading}
            className="file-input-hidden"
          />
          <span className="upload-button">
            {isUploading ? 'Uploading...' : '📤 Choose Image'}
          </span>
        </label>
      </div>

      {/* Or select from gallery */}
      <button
        type="button"
        onClick={handleSelectFromGallery}
        className="btn-gallery"
        disabled={loadingGallery}
      >
        {loadingGallery ? 'Loading gallery...' : '📷 Or Select from Gallery'}
      </button>

      {/* Image Gallery */}
      {showImageGallery && (
        <div className="image-gallery">
          <div className="gallery-header">
            <h4>Available Images</h4>
            <button
              type="button"
              onClick={() => setShowImageGallery(false)}
              className="btn-close"
            >
              ✕
            </button>
          </div>

          {loadingGallery ? (
            <div className="gallery-loading">Loading images...</div>
          ) : availableImages.length === 0 ? (
            <div className="gallery-empty">No images available</div>
          ) : (
            <div className="gallery-grid">
              {availableImages.map((img, idx) => (
                <div
                  key={idx}
                  className="gallery-item"
                  onClick={() => selectImage(img.url || img.file_path)}
                >
                  <img
                    src={img.url || img.file_path}
                    alt={img.filename || `Image ${idx + 1}`}
                  />
                  <div className="gallery-overlay">Click to select</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* URL Input Fallback */}
      <div className="form-group">
        <label className="form-label" style={{ fontSize: '12px', marginTop: '10px' }}>
          Or paste image URL directly:
        </label>
        <input
          type="text"
          value={previewUrl}
          onChange={(e) => {
            setPreviewUrl(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="https://..."
          className="url-input-fallback"
        />
      </div>
    </div>
  );
}
